import { NumberFormatter } from 'reduce-precision';
import { PortfolioApi } from 'src/components/portfolio/portfolio/api.js';
import { normalizeDetail, portfolioId, portfolioList, transactionList } from 'src/components/portfolio/portfolio/normalize.js';
import { transactionTotals } from 'src/components/portfolio/portfolio/calculations.js';
import { choosePortfolio, pnlTone, portfolioLinks, resolvePosition, SELECTED_PORTFOLIO_KEY } from './model.js';

const formatters = {
  usd: new NumberFormatter({ language: 'en', template: 'usd', precision: 'auto' }),
  irt: new NumberFormatter({ language: 'fa', template: 'irt', precision: 'auto' }),
  percentUsd: new NumberFormatter({ language: 'en', template: 'percent', precision: 'auto' }),
  percentIrt: new NumberFormatter({ language: 'fa', template: 'percent', precision: 'auto' }),
  quantity: new NumberFormatter({ language: 'en', template: 'number', precision: 'auto' }),
};

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
  })[character]);
}

function finite(value) {
  return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) ? Number(value) : null;
}

function money(value, currency, signed = false) {
  const number = finite(value);
  if (number === null) return '—';
  const plus = signed && number > 0 ? '+' : '';
  if (currency === 'irt') {
    const formatted = formatters.irt.toJson(number);
    const postfix = String(formatted.fullPostfix || formatted.postfix || '').replace(/\s*ت$/, '');
    return `<span class="arz-irt-price" dir="rtl"><span>${escapeHtml(`${plus}${formatted.sign}${formatted.wholeNumber}${postfix}`)}</span><span class="arz-irt-symbol" role="img" aria-label="تومان"></span></span>`;
  }
  return `<bdi dir="ltr">${escapeHtml(plus + formatters.usd.toString(number))}</bdi>`;
}

function percent(value, currency) {
  const number = finite(value);
  if (number === null) return '';
  const formatter = currency === 'irt' ? formatters.percentIrt : formatters.percentUsd;
  return `<bdi dir="ltr">${number > 0 ? '+' : ''}${escapeHtml(formatter.toString(number))}</bdi>`;
}

function quantity(value, symbol) {
  const number = finite(value);
  if (number === null) return '—';
  return `<bdi dir="ltr">${escapeHtml(formatters.quantity.toString(number))} ${escapeHtml(symbol)}</bdi>`;
}

function transactionDate(time) {
  const date = time ? new Date(time) : null;
  if (!date || Number.isNaN(date.getTime())) return '—';
  return `<time datetime="${escapeHtml(date.toISOString())}">${escapeHtml(date.toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' }))}</time>`;
}

function readStoredPortfolio() {
  try { return window.localStorage.getItem(SELECTED_PORTFOLIO_KEY) || ''; } catch { return ''; }
}

function storePortfolio(id) {
  try { if (id) window.localStorage.setItem(SELECTED_PORTFOLIO_KEY, id); } catch { /* Private mode keeps the default selection. */ }
}

export default function initAssetPosition() {
  const root = document.querySelector('[data-asset-position]');
  const toggle = document.querySelector('[data-asset-position-toggle]');
  // The rail only exists on desktop PDPs; mobile and other pages make no portfolio requests.
  if (!root || !toggle || root.dataset.assetPositionReady) return;
  root.dataset.assetPositionReady = 'true';

  const body = root.querySelector('[data-asset-position-body]');
  const api = new PortfolioApi(root.dataset.nexusBase || '');
  const asset = {
    id: String(root.dataset.assetId || ''),
    name: root.dataset.assetName || '',
    symbol: root.dataset.assetSymbol || '',
  };
  const currency = root.dataset.primaryCurrency === 'irt' ? 'irt' : 'usd';
  const secondary = currency === 'irt' ? 'usd' : 'irt';
  const links = portfolioLinks(root.dataset.portfolioUrl, asset);
  const toggleLabel = toggle.getAttribute('aria-label') || 'سبد دارایی';
  const state = {
    signedIn: null, loading: false, failed: false, portfolios: null, selectedId: '', detail: null,
    transactions: null, transactionsFailed: false, transactionsLoading: false, requestId: 0, open: false,
  };

  function selectedPortfolio() {
    return state.portfolios?.find((item) => portfolioId(item) === state.selectedId) || null;
  }

  function position() {
    if (state.failed) return { kind: 'error' };
    return resolvePosition({
      signedIn: state.signedIn, portfolios: state.portfolios, detail: state.detail, assetId: asset.id,
      transactions: state.transactions, transactionsFailed: state.transactionsFailed, currency,
    });
  }

  function syncToggle(view) {
    const owned = view.kind === 'held';
    toggle.dataset.positionState = owned ? 'held' : '';
    toggle.setAttribute('aria-label', owned ? `${toggleLabel}؛ ${asset.name} در پورتفولیوی شما است` : toggleLabel);
  }

  function action(label, href, variant = 'primary') {
    return `<a class="arz-asset-position__action arz-asset-position__action--${variant}" href="${escapeHtml(href)}" data-asset-position-link>${escapeHtml(label)}</a>`;
  }

  function portfolioPicker() {
    const portfolios = state.portfolios || [];
    if (portfolios.length < 2) {
      const name = selectedPortfolio()?.name || state.detail?.name || '';
      return name ? `<span class="arz-asset-position__portfolio">${escapeHtml(name)}</span>` : '';
    }
    const options = portfolios.map((item) => {
      const id = portfolioId(item);
      return `<option value="${escapeHtml(id)}"${id === state.selectedId ? ' selected' : ''}>${escapeHtml(item.name || 'پورتفولیو بدون نام')}</option>`;
    }).join('');
    return `<label class="arz-asset-position__portfolio-picker"><span class="arz-asset-position__sr">پورتفولیو</span><select data-asset-position-portfolio${state.loading ? ' disabled' : ''}>${options}</select></label>`;
  }

  function message(title, text, actions) {
    return `<div class="arz-asset-position__message"><p class="arz-asset-position__message-title">${title}</p>${text ? `<p class="arz-asset-position__message-text">${text}</p>` : ''}</div><div class="arz-asset-position__actions">${actions}</div>`;
  }

  function skeleton() {
    return '<div class="arz-asset-position__skeleton" aria-hidden="true"><span></span><span></span><span></span></div><p class="arz-asset-position__sr" role="status">در حال دریافت دارایی شما…</p>';
  }

  function transactionsMarkup(view) {
    if (state.transactionsLoading && !state.transactions) {
      return '<div class="arz-asset-position__skeleton arz-asset-position__skeleton--rows" aria-hidden="true"><span></span><span></span></div>';
    }
    if (view.transactionsFailed) {
      return '<p class="arz-asset-position__note">دریافت تراکنش ها ناموفق بود. <button type="button" class="arz-asset-position__inline-button" data-asset-position-retry-transactions>تلاش دوباره</button></p>';
    }
    if (!view.recent.length) return '';
    const rows = view.recent.map((transaction) => {
      const totals = transactionTotals(transaction.amount, transaction.unitPriceUsd, transaction.usdIrtRate);
      const buy = transaction.type === 'buy';
      return `<li class="arz-asset-position__transaction"><span class="arz-asset-position__transaction-main"><b class="arz-asset-position__type arz-asset-position__type--${buy ? 'buy' : 'sell'}">${buy ? 'خرید' : 'فروش'}</b>${quantity(transaction.amount, asset.symbol)}</span><span class="arz-asset-position__transaction-value">${money(totals[currency], currency)}</span><span class="arz-asset-position__transaction-date">${transactionDate(transaction.time)}</span></li>`;
    }).join('');
    return `<section class="arz-asset-position__recent" aria-label="آخرین تراکنش ها"><h3>آخرین تراکنش ها</h3><ul>${rows}</ul></section>`;
  }

  function positionMarkup(view) {
    const holding = view.holding;
    const pnlKey = currency === 'irt' ? 'pnlIrt' : 'pnlUsd';
    const pnlPercentKey = currency === 'irt' ? 'pnlPercentIrt' : 'pnlPercentUsd';
    const tone = pnlTone(holding[pnlKey]);
    const closed = view.kind === 'closed';
    const valueKey = currency === 'irt' ? 'valueIrt' : 'valueUsd';
    const secondaryKey = secondary === 'irt' ? 'valueIrt' : 'valueUsd';
    const summary = closed
      ? `<p class="arz-asset-position__closed">کل موجودی ${escapeHtml(asset.name)} را فروخته اید.</p>`
      : `<div class="arz-asset-position__value"><span class="arz-asset-position__label">ارزش دارایی شما</span><strong>${money(holding[valueKey], currency)}</strong><span class="arz-asset-position__secondary">${money(holding[secondaryKey], secondary)}</span></div>`;
    const count = view.transactionCount;
    const allLabel = count ? `همه تراکنش ها (${count.toLocaleString('fa-IR')})` : 'همه تراکنش ها';
    return `${summary}<dl class="arz-asset-position__stats"><div><dt>موجودی</dt><dd>${quantity(holding.quantity, asset.symbol)}</dd></div><div><dt>${closed ? 'سود و زیان تحقق یافته' : 'سود و زیان'}</dt><dd class="arz-asset-position__pnl arz-asset-position__pnl--${tone}">${money(holding[pnlKey], currency, true)}${percent(holding[pnlPercentKey], currency) ? `<small>${percent(holding[pnlPercentKey], currency)}</small>` : ''}</dd></div>${view.allocation === null ? '' : `<div><dt>سهم از پورتفولیو</dt><dd><bdi dir="ltr">${escapeHtml(formatters.percentIrt.toString(view.allocation))}</bdi></dd></div>`}</dl>${transactionsMarkup(view)}<div class="arz-asset-position__actions">${action('افزودن تراکنش', links.add)}${action(allLabel, links.transactions, 'secondary')}</div>`;
  }

  function render() {
    const view = position();
    syncToggle(view);
    if (!state.open) return;
    const name = escapeHtml(asset.name);
    root.setAttribute('aria-busy', String(state.loading || state.signedIn === null));
    root.querySelector('[data-asset-position-context]').innerHTML = state.signedIn && state.portfolios?.length ? portfolioPicker() : '';
    if (state.signedIn === null || (state.loading && !state.detail && !state.failed)) { body.innerHTML = skeleton(); return; }
    if (view.kind === 'guest') {
      body.innerHTML = message(`سود و زیان ${name} خود را دنبال کنید`, 'خرید و فروش هایتان را ثبت کنید تا ارزش لحظه ای و سود و زیان دارایی را به تومان و دلار ببینید.',
        `<button type="button" class="arz-asset-position__action arz-asset-position__action--primary" data-asset-position-login>ورود و شروع</button>${action('آشنایی با پورتفولیو', links.home, 'secondary')}`);
    } else if (view.kind === 'error') {
      body.innerHTML = message('دریافت اطلاعات پورتفولیو ناموفق بود.', 'اتصال خود را بررسی کنید و دوباره تلاش کنید.',
        `<button type="button" class="arz-asset-position__action arz-asset-position__action--secondary" data-asset-position-retry>تلاش دوباره</button>`);
    } else if (view.kind === 'no-portfolio') {
      body.innerHTML = message('هنوز پورتفولیویی نساخته اید', `با ثبت اولین تراکنش ${name}، پورتفولیوی خود را بسازید و سود و زیان آن را دنبال کنید.`,
        action('ساخت پورتفولیو', links.add));
    } else if (view.kind === 'not-held') {
      const portfolioName = selectedPortfolio()?.name || state.detail?.name || '';
      body.innerHTML = message(`${name} در ${portfolioName ? `پورتفولیوی «${escapeHtml(portfolioName)}»` : 'پورتفولیوی شما'} نیست`, 'اولین تراکنش را ثبت کنید تا ارزش و سود و زیان آن اینجا نمایش داده شود.',
        `${action('افزودن تراکنش', links.add)}${action('مشاهده پورتفولیو', links.home, 'secondary')}`);
    } else {
      body.innerHTML = positionMarkup(view);
    }
  }

  async function loadTransactions() {
    const requestId = state.requestId;
    const holding = state.detail && !state.detail.incomplete && state.detail.assets.some((item) => String(item.id) === asset.id);
    if (!holding || state.transactionsLoading) return;
    state.transactionsLoading = true; state.transactionsFailed = false; render();
    try {
      const list = transactionList(await api.transactions(state.selectedId, asset.id));
      if (requestId !== state.requestId) return;
      state.transactions = list;
    } catch {
      if (requestId !== state.requestId) return;
      state.transactionsFailed = true;
    }
    state.transactionsLoading = false; render();
  }

  async function loadDetail(requestId) {
    const listItem = selectedPortfolio() || {};
    state.detail = null; state.transactions = null; state.transactionsFailed = false; state.transactionsLoading = false;
    const detail = normalizeDetail(await api.detail(state.selectedId), listItem);
    if (requestId !== state.requestId) return;
    state.detail = detail; state.loading = false; render();
    if (state.open) loadTransactions();
  }

  async function load() {
    const requestId = ++state.requestId;
    state.loading = true; state.failed = false; render();
    try {
      const portfolios = portfolioList(await api.portfolios());
      if (requestId !== state.requestId) return;
      state.portfolios = portfolios;
      const chosen = choosePortfolio(portfolios, readStoredPortfolio());
      state.selectedId = chosen ? portfolioId(chosen) : '';
      if (!chosen) { state.detail = null; state.loading = false; render(); return; }
      await loadDetail(requestId);
    } catch {
      if (requestId !== state.requestId) return;
      state.failed = true; state.loading = false; render();
    }
  }

  async function switchPortfolio(id) {
    if (!id || id === state.selectedId) return;
    const requestId = ++state.requestId;
    state.selectedId = id; storePortfolio(id); state.loading = true; state.failed = false; render();
    try { await loadDetail(requestId); } catch {
      if (requestId !== state.requestId) return;
      state.failed = true; state.loading = false; render();
    }
  }

  // Opening and closing never move focus programmatically (DESIGN.md, Focus behavior).
  function setOpen(open) {
    if (state.open === open) return;
    state.open = open;
    root.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    if (!open) return;
    render();
    if (state.signedIn && !state.loading && (state.failed || !state.portfolios)) load();
    else if (state.detail && !state.transactions) loadTransactions();
  }

  toggle.addEventListener('click', () => setOpen(!state.open));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && state.open) setOpen(false);
  });
  document.addEventListener('pointerdown', (event) => {
    if (state.open && !root.contains(event.target) && !toggle.contains(event.target)) setOpen(false);
  });
  root.addEventListener('click', (event) => {
    const target = event.target;
    if (target.closest('[data-asset-position-close]')) return setOpen(false);
    if (target.closest('[data-asset-position-retry]')) return load();
    if (target.closest('[data-asset-position-retry-transactions]')) return loadTransactions();
    // /portfolio/ opens the stored selection, so the link lands on the portfolio shown here.
    if (target.closest('[data-asset-position-link]')) return storePortfolio(state.selectedId);
    if (target.closest('[data-asset-position-login]')) {
      return window.arz_runAuthenticatedAction?.({
        triggerElement: 'add_to_portfolio_shortcut',
        // The login modal sits outside the popover and closes it; reopen on the user's position.
        action: () => { state.signedIn = true; if (state.open) load(); else setOpen(true); },
      });
    }
  });
  root.addEventListener('change', (event) => {
    if (event.target.matches('[data-asset-position-portfolio]')) switchPortfolio(event.target.value);
  });

  let loginTimer = 0;
  const onLogin = () => {
    window.clearTimeout(loginTimer);
    loginTimer = window.setTimeout(() => {
      if (state.signedIn && (state.loading || state.portfolios)) return;
      state.signedIn = true; load();
    }, 150);
  };
  window.addEventListener('arz-login', onLogin);
  window.addEventListener('accounts.login', onLogin);
  window.addEventListener('accounts.logout', () => {
    state.requestId += 1;
    Object.assign(state, { signedIn: false, loading: false, failed: false, portfolios: null, selectedId: '', detail: null, transactions: null, transactionsFailed: false, transactionsLoading: false });
    render();
  });

  const checkLogin = typeof window.account?.checkUserLogin === 'function'
    ? Promise.race([Promise.resolve(window.account.checkUserLogin()), new Promise((resolve) => window.setTimeout(() => resolve(false), 8000))])
    : Promise.resolve(false);
  checkLogin.catch(() => false).then((user) => {
    if (state.signedIn !== null) return;
    state.signedIn = Boolean(user);
    if (!state.signedIn) { render(); return; }
    // Prefetch at idle so the toggle can show ownership without competing with the PDP's first paint.
    const start = () => { if (!state.portfolios && !state.loading) load(); };
    if (state.open) start();
    else if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 2000 });
    else window.setTimeout(start, 0);
  });
}
